import * as React from 'react'

import { cn } from './cn'
import IconButton from './IconButton'
import CheckCircleOutline from './icons/Check'
import CloseIcon from './icons/Close'
import WarningIcon from './icons/Warning'

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success'

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  severity?: AlertSeverity
  variant?: 'standard' | 'outlined' | 'filled'
  onClose?: React.MouseEventHandler<HTMLButtonElement>
  icon?: React.ReactNode | false
  action?: React.ReactNode
}

const severityClasses: Record<
  NonNullable<AlertProps['variant']>,
  Record<AlertSeverity, string>
> = {
  standard: {
    error: 'bg-error/10 text-error-dark dark:bg-error/20 dark:text-red-200',
    warning:
      'bg-warning/10 text-warning-dark dark:bg-warning/20 dark:text-orange-200',
    info: 'bg-info/10 text-info-dark dark:bg-info/20 dark:text-sky-200',
    success:
      'bg-success/10 text-success-dark dark:bg-success/20 dark:text-green-200',
  },
  outlined: {
    error: 'border border-error text-error-dark dark:text-red-200',
    warning: 'border border-warning text-warning-dark dark:text-orange-200',
    info: 'border border-info text-info-dark dark:text-sky-200',
    success: 'border border-success text-success-dark dark:text-green-200',
  },
  filled: {
    error: 'bg-error text-white',
    warning: 'bg-warning text-white',
    info: 'bg-info text-white',
    success: 'bg-success text-white',
  },
}

const iconColor: Record<AlertSeverity, string> = {
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
  success: 'text-success',
}

function DefaultIcon({ severity }: { severity: AlertSeverity }) {
  if (severity === 'success') {
    return <CheckCircleOutline fontSize="small" />
  }
  if (severity === 'warning' || severity === 'error') {
    return <WarningIcon fontSize="small" />
  }
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
      <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
    </svg>
  )
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      severity = 'success',
      variant = 'standard',
      onClose,
      icon,
      action,
      className,
      children,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-xl px-4 py-3 text-sm',
        severityClasses[variant][severity],
        className,
      )}
      {...props}
    >
      {icon !== false && (
        <span
          className={cn(
            'mt-0.5 shrink-0',
            variant !== 'filled' && iconColor[severity],
          )}
        >
          {icon ?? <DefaultIcon severity={severity} />}
        </span>
      )}
      <div className="min-w-0 grow py-0.5">{children}</div>
      {action}
      {onClose && (
        <IconButton
          size="small"
          color="inherit"
          aria-label="Close"
          onClick={onClose}
          className="-mr-1 shrink-0"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  ),
)
Alert.displayName = 'Alert'

export default Alert

export const AlertTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('-mt-0.5 mb-1 font-medium', className)}
    {...props}
  />
))
AlertTitle.displayName = 'AlertTitle'
